import { useEffect, useMemo, useRef, useState } from "react";

export interface UseTypewriterOptions {
  enabled?: boolean;
  baseDelayMs?: number;
  punctuationPauseMs?: number;
  tokenRegex?: RegExp;
}

export interface UseTypewriterReturn {
  displayed: string;
  isAnimating: boolean;
  reset: () => void;
  skipToEnd: () => void;
}

export function useTypewriter(
  content: string,
  {
    enabled = false,
    baseDelayMs = 20,
    punctuationPauseMs = 120,
    tokenRegex = /\S+\s*|\n/g,
  }: UseTypewriterOptions = {}
): UseTypewriterReturn {
  const [displayed, setDisplayed] = useState<string>(enabled ? "" : content);
  const [isAnimating, setIsAnimating] = useState<boolean>(enabled);

  const tokenIndexRef = useRef<number>(0);
  const cancelRef = useRef<boolean>(false);

  const tokens = useMemo(() => {
    return content.match(tokenRegex) || [];
  }, [content, tokenRegex]);

  const reset = () => {
    cancelRef.current = true;
    setDisplayed("");
    setIsAnimating(true);
    tokenIndexRef.current = 0;
  };

  const skipToEnd = () => {
    cancelRef.current = true;
    setDisplayed(content);
    setIsAnimating(false);
    tokenIndexRef.current = tokens.length;
  };

  useEffect(() => {
    cancelRef.current = false;
    if (!enabled) {
      setDisplayed(content);
      setIsAnimating(false);
      return () => {
        cancelRef.current = true;
      };
    }

    setDisplayed("");
    setIsAnimating(true);
    tokenIndexRef.current = 0;

    const step = () => {
      if (cancelRef.current) return;
      const i = tokenIndexRef.current;
      if (i >= tokens.length) {
        setIsAnimating(false);
        return;
      }
      const nextToken = tokens[i];
      tokenIndexRef.current = i + 1;
      setDisplayed((prev) => prev + nextToken);

      const trimmed = nextToken.trim();
      const needsPause = /[.,!?;:]$/.test(trimmed);
      const delay = needsPause ? baseDelayMs + punctuationPauseMs : baseDelayMs;
      setTimeout(step, Math.max(5, delay));
    };

    const timer = setTimeout(step, Math.max(5, baseDelayMs));
    return () => {
      cancelRef.current = true;
      clearTimeout(timer);
    };
  }, [content, enabled, baseDelayMs, punctuationPauseMs, tokens]);

  return { displayed, isAnimating, reset, skipToEnd };
}
